import React, { FC, useContext, useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@material-ui/core';
import InfiniteScroll from 'react-infinite-scroll-component';
import DiscussionItem from './DiscussionItem';
import { generatePath, Link, useHistory } from 'react-router-dom';
import DiscussionUserEmpty from './DiscussionUserEmpty';
import useStyles from './styles';
import { useGetDiscussions } from '../../hooks/queries/useGetDiscussions';
import {
  DiscussionFilterContext,
  IDiscussionFilter,
} from '../../providers/DiscussionFilterProvider/DiscussionFilterProvider';
import { DiscussionsInput } from '../../types/graphql-global-types';

export const PAGE_SIZE = 10;

interface IDiscussionsList {
  discussionId?: number;
}

const DiscussionsList: FC<IDiscussionsList> = (props) => {
  const { discussionId } = props;
  const classes = useStyles();

  const history = useHistory();

  const { tags, filter } = useContext<IDiscussionFilter>(
    DiscussionFilterContext,
  );

  const [getDiscussions, { data, loading, fetchMore }] = useGetDiscussions();

  const discussionsInput: DiscussionsInput = useMemo<DiscussionsInput>(
    () => ({
      tags,
      filter,
      isBackoffice: true,
      take: PAGE_SIZE,
      skip: 0,
    }),
    [filter, tags],
  );

  const handleNext = () => {
    fetchMore({
      variables: {
        input: {
          ...discussionsInput,
          skip: data?.discussions?.discussions?.length ?? 0,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if ((fetchMoreResult?.discussions?.discussions?.length ?? 0) === 0) {
          return prev;
        }
        return Object.assign({}, prev, {
          discussions: {
            __typename: prev?.discussions?.__typename ?? '',
            discussions: [
              ...(prev?.discussions?.discussions ?? []),
              ...(fetchMoreResult?.discussions?.discussions ?? []),
            ],
            totalCount: fetchMoreResult?.discussions?.totalCount ?? 0,
          },
        });
      },
    });
  };

  useEffect(() => {
    getDiscussions({ variables: { input: discussionsInput } });
  }, [discussionsInput, getDiscussions]);

  useEffect(() => {
    if (
      tags?.length &&
      !data?.discussions?.discussions?.find(
        (discussion) => discussion?.id === discussionId,
      )
    )
      history.push('/discussions');
  }, [discussionId, data, history, tags]);

  if (!data?.discussions?.discussions?.length && loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexGrow={1}>
        <CircularProgress />
      </Box>
    );

  if (!data?.discussions?.totalCount) return <DiscussionUserEmpty />;

  const Label: FC = ({ children }) => (
    <Box display="flex" justifyContent="center" alignItems="center" p={2}>
      {children}
    </Box>
  );

  return (
    <Box id="discussions-list-wrapper" className={classes.wrapper}>
      <InfiniteScroll
        scrollableTarget="discussions-list-wrapper"
        style={{ flexGrow: 1 }}
        dataLength={data?.discussions?.discussions?.length ?? 0}
        scrollThreshold={0.8}
        next={handleNext}
        hasMore={
          (data?.discussions?.discussions?.length ?? 0) <
          (data?.discussions?.totalCount ?? 0)
        }
        loader={<Label>Chargement...</Label>}
        endMessage={<Label>Fin.</Label>}>
        {data?.discussions?.discussions?.map(
          (item) =>
            item && (
              <Link
                key={`message-${item?.id}`}
                style={{ textDecoration: 'none' }}
                to={generatePath('/discussions/:id', { id: item.id })}>
                <DiscussionItem
                  key={`discussion-${item?.id}`}
                  active={item.id === discussionId}
                  discussionName={item?.name ?? ''}
                  discussionAvatarUrl={item?.avatarUrl ?? ''}
                  lastMessage={item.lastMessage}
                  unseenMessageNumber={item.unseenMessageNumber ?? 0}
                />
              </Link>
            ),
        )}
      </InfiniteScroll>
    </Box>
  );
};

export default DiscussionsList;
